import { useModalState } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Stack } from "@mui/material";
import { useGetAllowedWindowToScheduleOrientation } from "@src/appV2/ClipboardOrientation/hooks/useGetAllowedWindowToScheduleOrientation";
import { APP_V2_USER_EVENTS } from "@src/appV2/lib";
import { logError, logEvent } from "@src/appV2/lib/analytics";
import { DateTimePicker } from "@src/appV2/lib/DatePicker";
import { LoadingButton } from "@src/appV2/lib/LoadingButton/LoadingButton";
import { useGetQualificationDisplayLabels } from "@src/appV2/Qualifications/api/useGetQualificationDisplayLabels";
import { PickerButton } from "@src/appV2/redesign/components/PickerButton";
import { useScheduleAssessment } from "@src/appV2/SkillsAssessment/api/useScheduleAssessment";
import { useSkillsAssessmentFeatureFlags } from "@src/appV2/SkillsAssessment/api/useSkillsAssessmentFeatureFlags";
import { type SkillsAssessment } from "@src/appV2/SkillsAssessment/types";
import { adjustMinimumDatePickerTime } from "@src/appV2/SkillsAssessment/utils/adjustMinimumDatePickerTime";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
// eslint-disable-next-line no-restricted-imports -- legacy, use @clipboard-health/date-time. See https://www.notion.so/BP-Date-Time-2ef8643321f48032ad80e8a5b30aa278 for more information
import { format, isValid } from "date-fns";
import { useState } from "react";

import { LIMIT_SHIFTS_ACCESS_BASED_ON_TIMELINESS_EXPERIMENT_TYPE } from "../../constants";
import { OrientationListItems } from "../OrientationListItems";
import { ScheduleOrientationTitle } from "./ScheduleOrientationTitle";
import { SkipScheduleOrientationModal } from "./SkipScheduleOrientationModal";

interface Props {
  isRefetchingData: boolean;
  assessment: SkillsAssessment;
  qualificationName: string;
  onSuccess: () => void;
  onSkip?: () => void;
  onError?: () => void;
  initialScheduledFor?: string;
}

export function ScheduleOrientation(props: Props) {
  const {
    assessment,
    qualificationName,
    onSkip,
    onSuccess,
    isRefetchingData,
    onError,
    initialScheduledFor,
  } = props;

  const isAllowedToSkip = isDefined(onSkip);

  const worker = useDefinedWorker();

  const [scheduledFor, setScheduledFor] = useState<string | undefined>(initialScheduledFor);
  const skipOrientationModalState = useModalState();

  const { minDate, maxDate } = useGetAllowedWindowToScheduleOrientation();
  const { mutateAsync: scheduleAssessment, isLoading } = useScheduleAssessment();
  const {
    scheduledAssessmentConfig: { allowedTimeRange },
  } = useSkillsAssessmentFeatureFlags();

  const onTimeClipboardScoreAdjustment =
    assessment.attributes.experimentDetails?.experimentMetadata?.onTimeReward
      .clipboardScoreAdjustment;
  const isLimitedAccessExperimentEnabled =
    assessment.attributes.experimentDetails?.experimentType ===
      LIMIT_SHIFTS_ACCESS_BASED_ON_TIMELINESS_EXPERIMENT_TYPE &&
    isDefined(onTimeClipboardScoreAdjustment);

  const { qualificationNameToDisplayLabelMap } = useGetQualificationDisplayLabels([
    qualificationName,
  ]);
  const qualificationDisplayLabel = qualificationNameToDisplayLabelMap.get(qualificationName);

  return (
    <>
      <Stack alignItems="center" justifyContent="space-between" height="100%" spacing={6}>
        <Stack>
          <ScheduleOrientationTitle />

          <OrientationListItems
            items={[
              {
                text: "Come to the app at the scheduled time to show your punctuality for shifts.",
                bold: true,
              },
              {
                text: "It will only take 10 minutes.",
                light: true,
              },

              isLimitedAccessExperimentEnabled
                ? {
                    text: `You'll need to complete the demo shift on time to gain ${onTimeClipboardScoreAdjustment} Clipboard Score points and unlock full shift access.`,
                    light: true,
                  }
                : {
                    text: `You'll need to complete the demo shift on time to book ${qualificationDisplayLabel} shifts at most workplaces.`,
                    light: true,
                  },
            ]}
            sx={{
              py: 4,
            }}
          />

          <DateTimePicker
            minDateTime={adjustMinimumDatePickerTime(minDate)}
            maxDateTime={maxDate}
            disabled={isLoading}
            allowedTimeRange={allowedTimeRange}
            triggerButton={(props) => {
              return (
                <PickerButton
                  primaryText={
                    isDefined(scheduledFor) && isValid(new Date(scheduledFor))
                      ? format(new Date(scheduledFor), "MMM d, h:mm a")
                      : "Pick a time for your Demo Shift"
                  }
                  sx={(theme) => ({
                    border: `1px solid ${theme.palette.border?.main}`,
                    "&:focus": {
                      border: `1px solid ${theme.palette.border?.main}`,
                    },
                    "&:hover": {
                      border: `1px solid ${theme.palette.border?.main}`,
                    },
                    ...(!isDefined(scheduledFor) && {
                      backgroundColor: theme.palette.border?.darkInvert,
                    }),
                  })}
                  startIconType="clock-filled"
                  // Adding empty secondaryText to override default margin-left: auto on button's last child
                  secondaryText=" "
                  isSelected={isDefined(scheduledFor)}
                  {...props}
                />
              );
            }}
            onConfirm={(value) => {
              setScheduledFor(value.toISOString());
            }}
          />
        </Stack>

        <LoadingButton
          variant={isAllowedToSkip && !isDefined(scheduledFor) ? "outlined" : "contained"}
          isLoading={isLoading || isRefetchingData}
          sx={{
            borderRadius: 84,
            width: "100%",
          }}
          disabled={isAllowedToSkip ? false : !isDefined(scheduledFor)}
          onClick={async () => {
            if (!isDefined(scheduledFor)) {
              skipOrientationModalState.openModal();
              return;
            }

            try {
              await scheduleAssessment({
                assessmentId: assessment.id,
                workerId: worker.userId,
                scheduledFor,
              });

              logEvent(APP_V2_USER_EVENTS.CLIPBOARD_ORIENTATION_SCHEDULED_EVENT, {
                assessmentId: assessment.id,
                workerId: worker.userId,
                scheduledFor,
              });

              onSuccess();
            } catch (error) {
              logError("Failed to schedule orientation", {
                error,
                metadata: {
                  assessmentId: assessment.id,
                  workerId: worker.userId,
                  scheduledFor,
                },
              });
              onError?.();
            }
          }}
        >
          {isAllowedToSkip && !scheduledFor ? "Continue" : "Confirm"}
        </LoadingButton>
      </Stack>
      {isAllowedToSkip && (
        <SkipScheduleOrientationModal modalState={skipOrientationModalState} onSkip={onSkip} />
      )}
    </>
  );
}
